import { useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import styles from "./Sidebar.module.css";

import { ChipV2 } from "@/components/atoms/Chip";
import { type IconV2Name } from "@/components/atoms/IconV2";
import { LogoV2 } from "@/components/atoms/Logo";
import { NavItemV2 } from "@/components/atoms/NavItem";
import { LEGAL_PAGES } from "@/constants/legalConstants";
import { useHasActiveAlert } from "@/hooks/useHasActiveAlert";
import { documentationRoute } from "@/pages/Documentation/route";
import { homeRoute } from "@/pages/Home/route";
import { plantsRoute } from "@/pages/Plants/route";
import { useFeatureFlags } from "@/providers";
import { KNOWN_FLAGS } from "@/providers/_context/FeatureFlagsContext";

interface Props {
  onHomeClick: () => void;
  onPlantsClick: () => void;
  onDocumentationClick: () => void;
  onAlertsClick: () => void;
  onLogoutClick: () => void;
  onFeedbackClick: () => void;
  isCollapsed: boolean;
}

export const Sidebar = ({
  onHomeClick,
  onPlantsClick,
  onDocumentationClick,
  onAlertsClick,
  onLogoutClick,
  onFeedbackClick,
  isCollapsed,
}: Props) => {
  const { t } = useTranslation();
  const location = useLocation();

  const { isEnabled } = useFeatureFlags();

  const homeMapEnabled = isEnabled(KNOWN_FLAGS.HOME_MAP);
  const alertsEnabled = isEnabled(KNOWN_FLAGS.AVAILABILITY_ALERTS);

  const hasActiveAlert = useHasActiveAlert(alertsEnabled);

  const alertsChip = hasActiveAlert ? (
    <ChipV2 variant="ok" leftIcon="check" label={t("sidebar.alertsActive")} />
  ) : (
    <ChipV2 variant="accent" label={t("sidebar.alertsInactive")} />
  );

  const navItems: {
    id: string;
    icon: IconV2Name;
    label: string;
    onClick: () => void;
    active: boolean;
  }[] = [];

  if (homeMapEnabled) {
    navItems.push({
      id: "homeNavItem",
      icon: "home",
      label: t("sidebar.home"),
      onClick: onHomeClick,
      active: location.pathname.startsWith(homeRoute.fullPath),
    });
  }

  navItems.push({
    id: "plantsNavItem",
    icon: "plants",
    label: t("sidebar.plants"),
    onClick: onPlantsClick,
    active: location.pathname.startsWith(plantsRoute.fullPath),
  });

  navItems.push({
    id: "documentationNavItem",
    icon: "documentation",
    label: t("sidebar.documentation"),
    onClick: onDocumentationClick,
    active: location.pathname.startsWith(documentationRoute.fullPath),
  });

  // Items del pie del sidebar, anclados al fondo.
  const footerItems: {
    id: string;
    icon: IconV2Name;
    label: string;
    onClick: () => void;
    active?: boolean;
    chip?: ReactNode;
  }[] = [];

  if (alertsEnabled) {
    footerItems.push({
      id: "alertsNavItem",
      icon: "bell",
      label: t("sidebar.alerts"),
      onClick: onAlertsClick,
      active: location.pathname.startsWith("/alerts"),
      chip: hasActiveAlert === undefined ? undefined : alertsChip,
    });
  }

  footerItems.push(
    {
      id: "feedbackNavItem",
      icon: "feedback",
      label: t("sidebar.feedback"),
      onClick: onFeedbackClick,
    },
    {
      id: "logoutNavItem",
      icon: "logout",
      label: t("sidebar.logout"),
      onClick: onLogoutClick,
    },
  );

  return (
    <aside
      className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ""}`}
    >
      <div className={styles.brand}>
        <button
          type="button"
          onClick={onHomeClick}
          className={styles.logoButton}
          aria-label={t("sidebar.goHome")}
        >
          {isCollapsed ? (
            <LogoV2 variant="mark" />
          ) : (
            <LogoV2 title={t("sidebar.logoAlt")} />
          )}
        </button>
      </div>
      <div className={styles.nav}>
        <div className={styles.navItems}>
          {navItems.map(({ id, icon, label, onClick, active }) => (
            <NavItemV2
              key={id}
              icon={icon}
              label={label}
              active={active}
              onClick={onClick}
            />
          ))}
        </div>
        {footerItems.map(({ id, icon, label, onClick, active, chip }) => (
          <NavItemV2
            key={id}
            icon={icon}
            label={label}
            active={active}
            onClick={onClick}
            chip={chip}
          />
        ))}
        {!isCollapsed && (
          <nav className={styles.legal} aria-label={t("legal.navLabel")}>
            <a href={LEGAL_PAGES.notice} target="_blank" rel="noopener">
              {t("legal.notice")}
            </a>
            <a href={LEGAL_PAGES.privacy} target="_blank" rel="noopener">
              {t("legal.privacy")}
            </a>
            <a href={LEGAL_PAGES.cookies} target="_blank" rel="noopener">
              {t("legal.cookies")}
            </a>
          </nav>
        )}
      </div>
    </aside>
  );
};
